import { useState } from "react";
import { api, type CandidateDetail, type DuplicateGameSummary } from "../api.js";

/**
 * Resolving a possible duplicate — docs/sync-state-machine.md §6.
 *
 * D-2a is the design rule that matters here: **keep both is presented with
 * equal prominence to the merge options, and the wording assumes innocence.**
 * That is not politeness. Applying the detection rule to the club's own history
 * flags 23 of 486 consecutive pairs and 7 of those have identical scores, so
 * roughly a third of what reaches this screen is a legitimate immediate
 * rematch. A screen that nudged toward deleting would delete real games.
 *
 * D-2 — nothing is pre-selected and nothing happens without a tap. There is no
 * default answer because the app genuinely does not know.
 */
export function ResolveDuplicate({
  candidate,
  nameOf,
  onResolved,
  onSkip,
}: {
  candidate: CandidateDetail;
  nameOf: (id: string) => string;
  onResolved: () => Promise<void>;
  onSkip: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState(false);
  const [scoreA, setScoreA] = useState(String(candidate.gameA.sideAScore));
  const [scoreB, setScoreB] = useState(String(candidate.gameA.sideBScore));

  async function resolve(
    resolution: string,
    corrected?: { sideAScore: number; sideBScore: number },
  ): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.resolveDuplicate(candidate.id, { resolution, corrected });
      await onResolved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stack resolve">
      <h2>Possible duplicate</h2>
      {/* The question, asked the way D-2a requires. */}
      <p>{candidate.prompt}</p>

      <GameCard label="First" game={candidate.gameA} nameOf={nameOf} />
      <GameCard label="Second" game={candidate.gameB} nameOf={nameOf} />

      {error ? <p className="error">{error}</p> : null}

      {/* Keep both is first and full width: it is the likeliest right answer,
          not an escape hatch. */}
      <button className="primary" disabled={busy} onClick={() => void resolve("keep_both")}>
        They played it twice — keep both
      </button>

      <p className="muted small">Or, if it is the same game logged twice:</p>

      <div className="row">
        <button className="chip" disabled={busy} onClick={() => void resolve("keep_a")}>
          Keep the first
        </button>
        <button className="chip" disabled={busy} onClick={() => void resolve("keep_b")}>
          Keep the second
        </button>
        <button className="chip" disabled={busy} onClick={() => setCorrecting((v) => !v)}>
          Neither score is right
        </button>
      </div>

      {correcting ? (
        <div className="stack">
          <div className="row">
            <label>
              {candidate.gameA.sideA.map(nameOf).join(" & ")}
              <input
                inputMode="numeric"
                value={scoreA}
                onChange={(e) => setScoreA(e.target.value)}
              />
            </label>
            <label>
              {candidate.gameA.sideB.map(nameOf).join(" & ")}
              <input
                inputMode="numeric"
                value={scoreB}
                onChange={(e) => setScoreB(e.target.value)}
              />
            </label>
          </div>
          <button
            className="primary"
            disabled={busy}
            onClick={() =>
              void resolve("merge_edit", {
                sideAScore: Number(scoreA),
                sideBScore: Number(scoreB),
              })
            }
          >
            Keep one game at this score
          </button>
        </div>
      ) : null}

      <button className="link" onClick={onSkip} disabled={busy}>
        Decide later
      </button>
    </section>
  );
}

function GameCard({
  label,
  game,
  nameOf,
}: {
  label: string;
  game: DuplicateGameSummary;
  nameOf: (id: string) => string;
}): React.JSX.Element {
  const at = new Date(game.playedAt);
  return (
    <div className={game.counts ? "version" : "version version-excluded"}>
      <div className="row space-between">
        <strong>
          {game.sideA.map(nameOf).join(" & ")} {game.sideAScore}–{game.sideBScore}{" "}
          {game.sideB.map(nameOf).join(" & ")}
        </strong>
      </div>
      <span className="muted small">
        {label} · {at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        {game.enteredByName ? ` · entered by ${game.enteredByName}` : ""}
        {/* D-3 — say why it is not in the standings, rather than leaving a
            member to notice a number is missing. */}
        {game.counts ? "" : " · not counting while this is open"}
      </span>
    </div>
  );
}
